import React, { useCallback, useEffect, useState, useRef } from 'react';
import { mutate } from 'swr';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Loader2, Upload, Send, Save, Eye, EyeOff } from 'lucide-react';
import { adminGetSettings, adminUpdateSettings, adminTestEmail, uploadFileToStorage } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const AdminSettings = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const fileRef = useRef();
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const data = await adminGetSettings();
      setS(data);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load settings.' });
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  if (!s) return <div className="p-8 flex items-center gap-2 text-neutral-500"><Loader2 className="animate-spin" size={16}/> Loading...</div>;

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    uploadSettingImage(f, 'qr_image');
  };

  const save = async () => {
    setSaving(true);
    try {
      const patch = { ...s };
      delete patch.qr_image_local;
      const res = await adminUpdateSettings(patch);
      setS(res);
      mutate('publicSettings');
      toast({ title: 'Saved', description: 'Settings updated successfully.' });
    } catch (e) {
      console.error('Settings Save Error:', e);
      if (e.code === 'resource-exhausted' || e.message?.includes('too large')) {
        toast({ title: 'Error', description: 'Total settings data is too large. Try a smaller logo image.' });
      } else {
        toast({ title: 'Error', description: 'Failed to save settings.' });
      }
    } finally { setSaving(false); }
  };

  const uploadSettingImage = async (file, key) => {
    if (file.size > 5 * 1024 * 1024) {
      toast({ 
        title: 'File is too large', 
        description: 'Please upload an image under 5MB.', 
        variant: 'destructive' 
      });
      return;
    }
    setUploadingImage(true);
    try {
      const url = await uploadFileToStorage(file, 'settings');
      set(key, url);
      toast({ title: 'Image uploaded' });
    } catch (err) {
      console.error('Upload Error:', err);
      toast({ title: 'Upload failed', variant: 'destructive' });
    } finally {
      setUploadingImage(false);
    }
  };

  const testEmail = async () => {
    setTesting(true);
    try {
      const res = await adminTestEmail();
      if (res.ok) toast({ title: 'Email sent', description: `Sent test email to ${res.to}` });
      else toast({ title: 'Failed', description: res.error || 'SMTP test failed' });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to send test email';
      toast({ title: 'Error', description: String(msg) });
    } finally { setTesting(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl">Settings</h1>
          <p className="text-sm text-neutral-500">Configure UPI payment &amp; SMTP email</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#b8892d] hover:bg-[#a07826] gap-2">
          {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Save Changes
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Info */}
        <section className="bg-white border border-neutral-200 p-6">
          <h2 className="font-semibold text-lg mb-4 border-b border-neutral-100 pb-2">Contact Details</h2>
          <div className="space-y-4">
            <div className="space-y-1.5 mb-4">
              <Label>Site Name</Label>
              <Input value={s.siteName || ''} onChange={e => set('siteName', e.target.value)} placeholder="Santu Watch" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Contact Email</Label>
                <Input value={s.contactEmail || ''} onChange={e => set('contactEmail', e.target.value)} placeholder="contact@santuwatch.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Mobile Number</Label>
                <Input value={s.contactPhone || ''} onChange={e => set('contactPhone', e.target.value)} placeholder="+91 99999 99999" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Store Address</Label>
              <Input value={s.contactAddress || ''} onChange={e => set('contactAddress', e.target.value)} placeholder="New Delhi, India" />
            </div>
          </div>
        </section>

        {/* UPI */}
        <section className="bg-white border border-neutral-200 p-6">
          <h2 className="font-semibold text-lg mb-4 border-b border-neutral-100 pb-2">UPI Payment</h2>
          <div className="space-y-4">
            <div><Label>Merchant Name</Label><Input value={s.upi_name || ''} onChange={e => set('upi_name', e.target.value)} placeholder="Watch Trader" className="mt-1"/></div>
            <div><Label>UPI ID</Label><Input value={s.upi_id || ''} onChange={e => set('upi_id', e.target.value)} placeholder="yourid@upi" className="mt-1 font-mono"/></div>
            <div>
              <Label>QR Code Image</Label>
              <div className="mt-2 flex items-start gap-4">
                <div className="w-32 h-32 border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden">
                  {s.qr_image ? <img src={s.qr_image} alt="QR" className="w-full h-full object-contain"/> : <span className="text-xs text-neutral-400">No QR</span>}
                </div>
                <div className="space-y-2">
                  <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden"/>
                  <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2" disabled={uploadingImage}>
                    {uploadingImage ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14}/>} Upload QR
                  </Button>
                  {s.qr_image && <Button variant="outline" onClick={() => set('qr_image', '')} className="gap-2 ml-2 text-red-600 border-red-200" disabled={uploadingImage}>Remove</Button>}
                  <div className="text-xs text-neutral-500">PNG / JPG, upload QR Code.</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SMTP */}
        <section className="bg-white border border-neutral-200 p-6">
          <h2 className="font-semibold text-lg mb-4 border-b border-neutral-100 pb-2">SMTP Email</h2>
          <div className="space-y-4">
            <div><Label>Admin Email (order notifications)</Label><Input type="email" value={s.admin_email || ''} onChange={e => set('admin_email', e.target.value)} placeholder="admin@watchtrader.in" className="mt-1"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>SMTP Host</Label><Input value={s.smtp_host || ''} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.hostinger.com" className="mt-1"/></div>
              <div><Label>Port</Label><Input type="number" value={s.smtp_port || 587} onChange={e => set('smtp_port', Number(e.target.value))} className="mt-1"/></div>
            </div>
            <div><Label>SMTP Username</Label><Input value={s.smtp_user || ''} onChange={e => set('smtp_user', e.target.value)} placeholder="noreply@watchtrader.in" className="mt-1"/></div>
            <div><Label>SMTP Password</Label><Input type="password" value={s.smtp_password || ''} onChange={e => set('smtp_password', e.target.value)} placeholder="••••••••" className="mt-1"/></div>
            <div><Label>From Address</Label><Input value={s.smtp_from || ''} onChange={e => set('smtp_from', e.target.value)} placeholder="Watch Trader <noreply@watchtrader.in>" className="mt-1"/></div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Use STARTTLS</Label>
                <p className="text-xs text-neutral-500">Enable for port 587. Disable for port 465 (uses SSL automatically).</p>
              </div>
              <Switch checked={!!s.smtp_use_tls} onCheckedChange={(v) => set('smtp_use_tls', v)}/>
            </div>
            <div className="pt-3 border-t border-neutral-100 flex gap-2">
              <Button onClick={testEmail} disabled={testing} variant="outline" className="gap-2">
                {testing ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>} Send Test Email
              </Button>
              <span className="text-xs text-neutral-500 self-center">Save changes before testing.</span>
            </div>
          </div>
        </section>
        {/* Logo */}
        <section className="bg-white border border-neutral-200 p-6 lg:col-span-2">
          <h2 className="font-semibold text-lg mb-4 border-b border-neutral-100 pb-2">Site Logo</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Logo Image</Label>
              <div className="mt-2 flex items-start gap-4">
                <div className="w-32 h-32 border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden">
                  {s.logo_image ? <img src={s.logo_image} alt="Logo" className="w-full h-full object-contain" /> : <span className="text-xs text-neutral-400">No Logo</span>}
                </div>
                <div className="space-y-2">
                  <input id="logoFile" type="file" accept="image/*" onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    uploadSettingImage(f, 'logo_image');
                  }} className="hidden" />
                  <Button variant="outline" onClick={() => document.getElementById('logoFile')?.click()} className="gap-2" disabled={uploadingImage}>
                    {uploadingImage ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Upload Logo
                  </Button>
                  {s.logo_image && (
                    <Button variant="outline" onClick={() => set('logo_image', '')} className="gap-2 ml-2 text-red-600 border-red-200" disabled={uploadingImage}>
                      Remove
                    </Button>
                  )}
                  <div className="text-xs text-neutral-500">PNG / JPG, up to 3MB.</div>
                </div>
              </div>
            </div>
            <div>
              <Label>Logo Max Width (px)</Label>
              <Input 
                type="number" 
                value={s.logo_width || ''} 
                onChange={e => set('logo_width', Number(e.target.value))} 
                placeholder="e.g. 150" 
                className="mt-1"
              />
              <p className="text-xs text-neutral-500 mt-1">Leave empty to use default sizing (Header: 150px, Footer: 200px).</p>
            </div>
          </div>
        </section>

        {/* Security */}
        <section className="bg-white border border-neutral-200 p-6 lg:col-span-2">
          <h2 className="font-semibold text-lg mb-4 border-b border-neutral-100 pb-2">Security</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>New Admin Password</Label>
              <div className="relative">
                <Input 
                  type={showPassword ? "text" : "password"} 
                  value={s.admin_password || ''} 
                  onChange={e => set('admin_password', e.target.value)} 
                  placeholder="Leave blank to keep unchanged" 
                  className="mt-1 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 mt-0.5"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="text-xs text-neutral-500 mt-1">Leave this blank if you don't want to change the password.</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminSettings;
